/**
 * LineItemRow - Editable row for a closing cost line item
 *
 * Displays and allows editing of a single line item with:
 * - Description
 * - Borrower Paid at/before Closing
 * - Seller Paid at/before Closing
 * - Paid by Others
 */

import { Trash2 } from 'lucide-react';
import type * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { LineItem, LineItemSection } from '../../../types';
import { CurrencyInput } from './CurrencyInput';

export interface LineItemRowProps {
  /** The line item data */
  item: LineItem;
  /** Index in the list */
  index: number;
  /** Section this item belongs to */
  section: LineItemSection;
  /** Change handler */
  onChange: (item: LineItem) => void;
  /** Delete handler */
  onDelete: () => void;
  /** Whether the row can be deleted */
  canDelete?: boolean;
  /** Whether the row is disabled */
  disabled?: boolean;
  /** Is this a header/section title row */
  isHeader?: boolean;
}

/**
 * Format line number for display (e.g., "A.01")
 */
function formatLineNumber(section: LineItemSection, index: number): string {
  return `${section}.${String(index + 1).padStart(2, '0')}`;
}

/**
 * Calculate row total (all paid amounts)
 */
function calculateRowTotal(item: LineItem): number {
  return (
    (item.borrowerPaidAtClosing ?? 0) +
    (item.borrowerPaidBeforeClosing ?? 0) +
    (item.sellerPaidAtClosing ?? 0) +
    (item.sellerPaidBeforeClosing ?? 0) +
    (item.paidByOthers ?? 0)
  );
}

/**
 * LineItemRow - Editable table row for line items
 */
export const LineItemRow: React.FC<LineItemRowProps> = ({
  item,
  index,
  section,
  onChange,
  onDelete,
  canDelete = true,
  disabled = false,
  isHeader = false,
}) => {
  const handleFieldChange = <K extends keyof LineItem>(field: K, value: LineItem[K]) => {
    onChange({ ...item, [field]: value });
  };

  const lineNumber = item.lineNumberString ?? formatLineNumber(section, index);
  const rowTotal = calculateRowTotal(item);

  // Header row styling
  if (isHeader) {
    return (
      <tr className="bg-muted font-semibold">
        <td className="px-2 py-2 text-sm">{section}</td>
        <td className="px-2 py-2 text-sm" colSpan={6}>
          {item.description}
        </td>
        <td className="px-2 py-2 text-right text-sm">
          ${rowTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </td>
        <td className="px-2 py-1 w-10"></td>
      </tr>
    );
  }

  return (
    <tr className={cn('border-b hover:bg-accent', disabled && 'opacity-50')}>
      {/* Line Number */}
      <td className="px-2 py-1 text-sm text-muted-foreground w-14">{lineNumber}</td>

      {/* Description */}
      <td className="px-2 py-1 min-w-[200px]">
        <Input
          type="text"
          value={item.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          disabled={disabled}
          placeholder="Description"
          className="h-8 text-sm"
        />
      </td>

      {/* Borrower Paid at Closing */}
      <td className="px-1 py-1 w-28">
        <CurrencyInput
          value={item.borrowerPaidAtClosing ?? null}
          onChange={(v) => handleFieldChange('borrowerPaidAtClosing', v ?? undefined)}
          disabled={disabled}
          inline
          size="sm"
          inputClassName="h-8 text-sm"
        />
      </td>

      {/* Borrower Paid Before Closing */}
      <td className="px-1 py-1 w-28">
        <CurrencyInput
          value={item.borrowerPaidBeforeClosing ?? null}
          onChange={(v) => handleFieldChange('borrowerPaidBeforeClosing', v ?? undefined)}
          disabled={disabled}
          inline
          size="sm"
          inputClassName="h-8 text-sm"
        />
      </td>

      {/* Seller Paid at Closing */}
      <td className="px-1 py-1 w-28">
        <CurrencyInput
          value={item.sellerPaidAtClosing ?? null}
          onChange={(v) => handleFieldChange('sellerPaidAtClosing', v ?? undefined)}
          disabled={disabled}
          inline
          size="sm"
          inputClassName="h-8 text-sm"
        />
      </td>

      {/* Seller Paid Before Closing */}
      <td className="px-1 py-1 w-28">
        <CurrencyInput
          value={item.sellerPaidBeforeClosing ?? null}
          onChange={(v) => handleFieldChange('sellerPaidBeforeClosing', v ?? undefined)}
          disabled={disabled}
          inline
          size="sm"
          inputClassName="h-8 text-sm"
        />
      </td>

      {/* Paid by Others */}
      <td className="px-1 py-1 w-28">
        <CurrencyInput
          value={item.paidByOthers ?? null}
          onChange={(v) => handleFieldChange('paidByOthers', v ?? undefined)}
          disabled={disabled}
          inline
          size="sm"
          inputClassName="h-8 text-sm"
        />
      </td>

      {/* Row Total */}
      <td className="px-2 py-1 text-right text-sm font-medium w-20">
        ${rowTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
      </td>

      {/* Actions */}
      <td className="px-1 py-1 w-10">
        {canDelete && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </td>
    </tr>
  );
};

LineItemRow.displayName = 'LineItemRow';

export { formatLineNumber, calculateRowTotal };
